import type { Action } from '../types/roles';
import type { ResourceItem } from '../types/common';

export type ResourceLoader = () => Promise<ResourceItem[]>

export interface Resource {
    type: string;
    labelKey: string,
    actions: Action[],
    icon?: string,
    loader: ResourceLoader
}

export const useResourcesStore = defineStore('resourcesStore', () => {
    const registeredResources = ref<Resource[]>([]);

    const getResource = (type: string): Resource | undefined => {
        return registeredResources.value.find(it => it.type === type);
    };

    /**
     * Registers resource that permissions should be configurable for
     * @param type resource type identifier
     * @param resourceLabelKey localization label for resource name
     * @param availableActions actions that can be defined for each item
     * @param loader loader function that loads list of items to set permissions on
     * @param icon resource icon
     */
    function registerResource (type: string, resourceLabelKey: string, availableActions: Action[], loader: ResourceLoader,
        icon: string | undefined = undefined
    ): Resource {
        const item: Resource = { type, labelKey: resourceLabelKey, actions: availableActions, loader, icon };
        registeredResources.value.push(item);
        return item;
    }

    async function resolveResourceItems (items: {id: string, resourceType: string}[]): Promise<ResourceItem[]> {
        const requiredTypes = Array.from(new Set(items.map(it => it.resourceType)));

        const resources = Object.fromEntries(
            (await Promise.all(requiredTypes.map(it => getResource(it)!.loader())))
                .map((it, index) => ([requiredTypes[index], it] as [string, ResourceItem[]]))
        );

        return items.map(it => resources[it.resourceType]!.find(i => i.id === it.id)!);
    }

    return {
        resolveResourceItems,
        registerResource,
        registeredResources,
        getResource
    };
});
